import { useState } from 'react';
import { useParams, useRouter } from '@tanstack/react-router';
import {
  Phone, MessageCircle, Edit2, Tag, MapPin, Calendar, User,
  Activity, FileText, PhoneCall, Clock, Check, AlertCircle,
  Shield, CreditCard, Building2, ChevronRight, Send,
  UserCheck, MoreHorizontal, ArrowLeft,
  ChevronLeft, Star, Stethoscope, Hash, Zap,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useLeadDetail, useLeadActivity, useUpdateLead, useAddRemark } from '@/hooks/useLeads';
import { SourceBadge } from '@/components/ui/Badge';
import { EditLeadModal } from '@/components/shared/LeadForm';
import { Spinner } from '@/components/ui/Spinner';
import { formatDate, formatDateTime, formatRelativeTime, formatPhone, getInitials } from '@/utils/formatters';
import type { LeadStatus } from '@/types';

// ─── Pipeline ─────────────────────────────────────────────────────────────────
const PIPELINE: { value: LeadStatus; label: string; color: string }[] = [
  { value: 'new',           label: 'New',           color: '#3b82f6' },
  { value: 'followup',      label: 'Follow Up',     color: '#f59e0b' },
  { value: 'opd_scheduled', label: 'OPD Scheduled', color: '#06b6d4' },
  { value: 'opd_done',      label: 'OPD Done',      color: '#10b981' },
  { value: 'ipd_scheduled', label: 'IPD Scheduled', color: '#8b5cf6' },
  { value: 'ipd_done',      label: 'IPD Done',      color: '#6366f1' },
  { value: 'insurance',     label: 'Insurance',     color: '#f97316' },
  { value: 'closed_won',    label: 'Closed Won',    color: '#059669' },
  { value: 'closed_lost',   label: 'Closed Lost',   color: '#ef4444' },
];

const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New', followup: 'Follow Up', opd_scheduled: 'OPD Scheduled',
  opd_done: 'OPD Done', ipd_scheduled: 'IPD Scheduled', ipd_done: 'IPD Done',
  insurance: 'Insurance', preauth: 'Pre-Auth', emi: 'EMI',
  closed_won: 'Closed Won', closed_lost: 'Closed Lost', not_interested: 'Not Interested',
};

const STATUS_COLORS: Record<LeadStatus, string> = {
  new: '#3b82f6', followup: '#f59e0b', opd_scheduled: '#06b6d4',
  opd_done: '#10b981', ipd_scheduled: '#8b5cf6', ipd_done: '#6366f1',
  insurance: '#f97316', preauth: '#14b8a6', emi: '#3b82f6',
  closed_won: '#059669', closed_lost: '#ef4444', not_interested: '#94a3b8',
};

const ACTIVITY_CFG: Record<string, { icon: React.ReactNode; color: string; bg: string; label: string }> = {
  call:          { icon: <PhoneCall size={14} />,  color: '#3b82f6', bg: '#eff6ff',  label: 'Outgoing Call' },
  followup:      { icon: <Clock size={14} />,      color: '#f59e0b', bg: '#fffbeb',  label: 'Follow-up' },
  status_change: { icon: <Activity size={14} />,   color: '#8b5cf6', bg: '#f5f3ff',  label: 'Status Changed' },
  assignment:    { icon: <UserCheck size={14} />,  color: '#00b5d8', bg: '#ecfeff',  label: 'Assignment' },
  system:        { icon: <Zap size={14} />,        color: '#64748b', bg: '#f1f5f9',  label: 'System' },
  general:       { icon: <FileText size={14} />,   color: '#10b981', bg: '#ecfdf5',  label: 'General Note' },
};

const AVATAR_PALETTE = [
  { fg: '#1e3a5f', bg: '#dbeafe' }, { fg: '#00b5d8', bg: '#cffafe' },
  { fg: '#059669', bg: '#d1fae5' }, { fg: '#7c3aed', bg: '#ede9fe' },
  { fg: '#dc2626', bg: '#fee2e2' }, { fg: '#d97706', bg: '#fef3c7' },
];

function avatarStyle(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

// ─── ECG SVG decoration ───────────────────────────────────────────────────────
function EcgLine() {
  return (
    <svg viewBox="0 0 420 60" className="absolute right-0 top-0 h-full w-64 opacity-10 pointer-events-none"
      preserveAspectRatio="none">
      <path
        d="M0,30 L80,30 L95,30 L110,8 L120,52 L130,18 L140,42 L150,30 L180,30 L200,30 L215,5 L225,55 L235,20 L245,40 L255,30 L420,30"
        stroke="#00b5d8" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export function LeadDetailPage() {
  const { leadId } = useParams({ strict: false }) as { leadId: string };
  const router = useRouter();

  const [activeTab, setActiveTab] = useState<'overview' | 'medical' | 'activity' | 'documents' | 'calls' | 'notes'>('overview');
  const [activityPage, setActivityPage] = useState(1);
  const [remarkType, setRemarkType] = useState<'general' | 'call' | 'followup'>('general');
  const [noteText, setNoteText] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);

  const { data: lead, isLoading } = useLeadDetail(leadId);
  const { data: activityData, isLoading: actLoading } = useLeadActivity(leadId, activityPage);
  const updateLead = useUpdateLead();
  const addRemark = useAddRemark();

  const handleStatusChange = (status: LeadStatus) => {
    if (!lead) return;
    updateLead.mutate({ id: lead.id, status });
  };

  const handleEditSubmit = (data: Record<string, unknown>) => {
    if (!lead) return;
    updateLead.mutate(
      { id: lead.id, ...data },
      { onSuccess: () => setShowEditModal(false) },
    );
  };

  const handleAddNote = () => {
    if (!lead || !noteText.trim()) return;
    addRemark.mutate(
      { leadId: lead.id, remark: noteText.trim(), type: remarkType },
      { onSuccess: () => { setNoteText(''); setActivityPage(1); } },
    );
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-96">
      <Spinner size="lg" />
    </div>
  );

  if (!lead) return (
    <div className="flex flex-col items-center justify-center h-96 gap-3">
      <AlertCircle size={48} className="text-slate-200" />
      <p className="text-slate-500">Lead not found</p>
      <button onClick={() => router.history.back()} className="text-sm text-[#00b5d8] flex items-center gap-1 hover:underline">
        <ArrowLeft size={13} /> Go back
      </button>
    </div>
  );

  const av = avatarStyle(lead.name);
  const currentIdx = PIPELINE.findIndex(p => p.value === lead.status);
  const statusColor = STATUS_COLORS[lead.status] ?? '#64748b';
  const statusLabel = STATUS_LABELS[lead.status] ?? lead.status;
  const activities = activityData?.data ?? [];
  const pg = activityData?.pagination;

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ── Breadcrumb bar ──────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-100 px-6 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <button onClick={() => router.history.back()}
            className="flex items-center gap-1 text-slate-500 hover:text-[#1e3a5f] font-medium transition-colors">
            <ArrowLeft size={13} /> Leads
          </button>
          <ChevronRight size={12} className="text-slate-300" />
          <span className="text-slate-800 font-semibold">{lead.name}</span>
          {lead.ptId && (
            <>
              <ChevronRight size={12} className="text-slate-300" />
              <span className="text-[#00b5d8] font-mono text-xs font-semibold bg-[#00b5d8]/8 px-2 py-0.5 rounded">
                {lead.ptId}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 px-2.5 py-1 rounded-lg hover:bg-slate-50">
            <ChevronLeft size={12} /> Previous
          </button>
          <button className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 px-2.5 py-1 rounded-lg hover:bg-slate-50">
            Next <ChevronRight size={12} />
          </button>
          <button className="w-7 h-7 flex items-center justify-center border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-400">
            <MoreHorizontal size={14} />
          </button>
        </div>
      </div>

      {/* ── Main layout ─────────────────────────────────────────────────── */}
      <div className="flex gap-0 h-[calc(100vh-97px)]">

        {/* ════════════════════ CENTER CONTENT ════════════════════════════ */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 min-w-0">

          {/* ── Profile Card ──────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm relative overflow-hidden">
            <EcgLine />
            <div className="p-5 flex items-start gap-5">

              {/* Avatar + Active badge */}
              <div className="flex flex-col items-center gap-1.5 shrink-0">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-extrabold shadow-sm"
                  style={{ background: av.bg, color: av.fg }}>
                  {getInitials(lead.name)}
                </div>
                <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Active
                </span>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-extrabold text-[#1e3a5f] leading-tight">{lead.name}</h1>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-slate-500">
                      {lead.age && (
                        <span className="flex items-center gap-1">
                          <User size={12} /> {lead.age} yrs
                        </span>
                      )}
                      {lead.city && (
                        <span className="flex items-center gap-1">
                          <MapPin size={12} /> {lead.city}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-2">
                      <span className="flex items-center gap-1.5 text-sm text-slate-600">
                        <Phone size={13} className="text-[#00b5d8]" />
                        {formatPhone(lead.phone)}
                      </span>
                      {lead.email && (
                        <span className="flex items-center gap-1.5 text-sm text-slate-600">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[#00b5d8]">
                            <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                          </svg>
                          {lead.email}
                        </span>
                      )}
                    </div>
                    {/* Tags / source chips */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {lead.source && <SourceBadge source={lead.source} />}
                      {lead.disease && (
                        <span className="flex items-center gap-1 px-2.5 py-0.5 bg-[#00b5d8]/10 text-[#00b5d8] text-xs font-semibold rounded-full border border-[#00b5d8]/20">
                          <Stethoscope size={10} /> {lead.disease}
                        </span>
                      )}
                      {lead.tags?.map(t => (
                        <span key={t} className="flex items-center gap-1 px-2.5 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                          <Tag size={9} /> {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Right: status + buttons */}
                  <div className="flex flex-col items-end gap-3 shrink-0">
                    <span className="px-3 py-1 rounded-full text-sm font-bold text-white shadow-sm"
                      style={{ background: statusColor }}>
                      {statusLabel}
                    </span>
                    <div className="flex items-center gap-2">
                      <a href={`tel:${lead.phone}`}
                        className="flex items-center gap-1.5 h-8 px-3.5 rounded-xl text-xs font-bold bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm transition-colors">
                        <Phone size={12} /> Call
                      </a>
                      <a href={`https://wa.me/${lead.phone?.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1.5 h-8 px-3.5 rounded-xl text-xs font-bold bg-green-500 text-white hover:bg-green-600 shadow-sm transition-colors">
                        <MessageCircle size={12} /> WhatsApp
                      </a>
                      <button onClick={() => setShowEditModal(true)}
                        className="flex items-center gap-1.5 h-8 px-3.5 rounded-xl text-xs font-bold border-2 border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
                        <Edit2 size={11} /> Edit Lead
                      </button>
                    </div>
                    {/* Flag badges */}
                    <div className="flex gap-1.5">
                      {lead.isInsuranceCase && (
                        <span className="text-xs font-semibold px-2 py-0.5 bg-orange-50 text-orange-600 rounded-full border border-orange-200">
                          Insurance
                        </span>
                      )}
                      {lead.isEmiCase && (
                        <span className="text-xs font-semibold px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full border border-blue-200">
                          EMI
                        </span>
                      )}
                      {lead.isPreauthPending && (
                        <span className="text-xs font-semibold px-2 py-0.5 bg-red-50 text-red-600 rounded-full border border-red-200">
                          Pre-Auth Pending
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Patient Journey ──────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <p className="text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-4 flex items-center gap-2">
              <Activity size={13} className="text-[#00b5d8]" /> Patient Journey
            </p>
            <div className="flex items-start">
              {PIPELINE.map((stage, idx) => {
                const isPast = idx < currentIdx;
                const isCurrent = idx === currentIdx;
                const isFuture = idx > currentIdx;
                const nextStage = PIPELINE[idx + 1];
                return (
                  <div key={stage.value} className="flex-1 flex flex-col items-center relative">
                    {/* Left connector */}
                    {idx > 0 && (
                      <div className="absolute top-[18px] right-1/2 left-0 h-0.5"
                        style={{ background: isPast || isCurrent ? PIPELINE[idx - 1]?.color ?? stage.color : '#e2e8f0' }} />
                    )}
                    {/* Right connector */}
                    {idx < PIPELINE.length - 1 && (
                      <div className="absolute top-[18px] left-1/2 right-0 h-0.5"
                        style={{ background: isPast ? nextStage?.color ?? stage.color : '#e2e8f0' }} />
                    )}
                    {/* Node */}
                    <button onClick={() => handleStatusChange(stage.value)}
                      className={clsx(
                        'w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs z-10 border-2 transition-all',
                        isCurrent ? 'scale-110' : 'hover:scale-105',
                      )}
                      style={{
                        background: isFuture ? '#f8fafc' : stage.color,
                        borderColor: isFuture ? '#e2e8f0' : stage.color,
                        color: isFuture ? '#cbd5e1' : '#fff',
                        boxShadow: isCurrent ? `0 0 0 4px ${stage.color}30` : undefined,
                      }}>
                      {isPast ? <Check size={14} strokeWidth={3} /> : idx + 1}
                    </button>
                    <span className="text-[10px] font-semibold text-center mt-1.5 leading-tight"
                      style={{ color: isFuture ? '#94a3b8' : isCurrent ? stage.color : '#475569' }}>
                      {stage.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Tabs ─────────────────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex border-b border-slate-100 overflow-x-auto">
              {([
                { id: 'overview',   label: 'Overview',      icon: <Star size={13} /> },
                { id: 'medical',    label: 'Medical Info',   icon: <Stethoscope size={13} /> },
                { id: 'activity',   label: 'Activity Feed',  icon: <Activity size={13} />, count: pg?.total },
                { id: 'documents',  label: 'Documents',      icon: <FileText size={13} /> },
                { id: 'calls',      label: 'Calls',          icon: <PhoneCall size={13} /> },
                { id: 'notes',      label: 'Notes',          icon: <Edit2 size={13} /> },
              ] as const).map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    'flex items-center gap-1.5 px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-all',
                    activeTab === tab.id
                      ? 'border-[#00b5d8] text-[#00b5d8]'
                      : 'border-transparent text-slate-400 hover:text-slate-600',
                  )}>
                  {tab.icon} {tab.label}
                  {'count' in tab && tab.count !== undefined && tab.count > 0 && (
                    <span className={clsx(
                      'text-xs font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center',
                      activeTab === tab.id ? 'bg-[#00b5d8] text-white' : 'bg-slate-100 text-slate-500',
                    )}>{tab.count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* ── OVERVIEW TAB ─────────────────────────────────────────── */}
            {activeTab === 'overview' && (
              <div className="p-5 space-y-5">
                {/* 3-column info grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                  {/* Lead Information */}
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <h3 className="flex items-center gap-1.5 text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-3">
                      <Hash size={11} /> Lead Information
                    </h3>
                    <div className="space-y-2.5">
                      {[
                        { label: 'Lead ID', value: lead.ptId },
                        { label: 'Lead Source', value: lead.source?.replace(/_/g, ' ') },
                        { label: 'Category', value: lead.category },
                        { label: 'Created On', value: formatDateTime(lead.createdAt) },
                        { label: 'Last Contact', value: lead.lastContactedAt ? formatDateTime(lead.lastContactedAt) : '—' },
                      ].map(item => (
                        <div key={item.label} className="flex items-start justify-between gap-2">
                          <span className="text-xs text-slate-400 shrink-0">{item.label}</span>
                          <span className="text-xs font-semibold text-slate-700 text-right">{item.value ?? '—'}</span>
                        </div>
                      ))}
                      {/* Assigned To */}
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-xs text-slate-400 shrink-0">Assigned To</span>
                        {lead.assignedTo ? (
                          <div className="flex items-center gap-1.5">
                            <div className="w-4 h-4 rounded-full bg-[#00b5d8] flex items-center justify-center text-[8px] font-bold text-white">
                              {getInitials(lead.assignedTo.name)}
                            </div>
                            <span className="text-xs font-semibold text-slate-700">
                              {lead.assignedTo.name}
                            </span>
                          </div>
                        ) : <span className="text-xs text-slate-400">Unassigned</span>}
                      </div>
                    </div>
                  </div>

                  {/* Key Dates */}
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <h3 className="flex items-center gap-1.5 text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-3">
                      <Calendar size={11} /> Key Dates
                    </h3>
                    <div className="space-y-2.5">
                      {[
                        { label: 'Created Date', value: formatDate(lead.createdAt), color: null },
                        { label: 'Due Date', value: formatDate(lead.dueDate), color: lead.dueDate ? '#f59e0b' : null },
                        { label: 'OPD Date', value: formatDate(lead.opdDate), color: '#06b6d4' },
                        { label: 'IPD Date', value: formatDate(lead.ipdDate), color: '#8b5cf6' },
                        { label: 'Updated', value: formatRelativeTime(lead.updatedAt), color: null },
                      ].map(item => (
                        <div key={item.label} className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-1 text-xs text-slate-400 shrink-0">
                            {item.color && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: item.color }} />}
                            {item.label}
                          </span>
                          <span className="text-xs font-semibold text-right"
                            style={{ color: item.color ?? '#374151' }}>
                            {item.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Lead Statistics */}
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <h3 className="flex items-center gap-1.5 text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-3">
                      <Activity size={11} /> Lead Statistics
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-xl p-3 text-center text-white"
                        style={{ background: 'linear-gradient(135deg, #1e3a5f, #0a6fa8)' }}>
                        <div className="text-2xl font-extrabold">{lead.followupsCount}</div>
                        <div className="text-xs opacity-80 mt-0.5">Follow-ups</div>
                      </div>
                      <div className="rounded-xl p-3 text-center text-white"
                        style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)' }}>
                        <div className="text-2xl font-extrabold">{pg?.total ?? '…'}</div>
                        <div className="text-xs opacity-80 mt-0.5">Activities</div>
                      </div>
                      <div className="rounded-xl p-3 text-center text-white"
                        style={{ background: lead.opdDone ? 'linear-gradient(135deg, #0891b2, #06b6d4)' : 'linear-gradient(135deg, #94a3b8, #cbd5e1)' }}>
                        <div className="text-lg font-extrabold">{lead.opdDone ? 'Yes' : 'No'}</div>
                        <div className="text-xs opacity-80 mt-0.5">OPD Done</div>
                      </div>
                      <div className="rounded-xl p-3 text-center text-white"
                        style={{ background: lead.consentObtained ? 'linear-gradient(135deg, #059669, #10b981)' : 'linear-gradient(135deg, #dc2626, #f87171)' }}>
                        <div className="text-lg font-extrabold">{lead.consentObtained ? 'Yes' : 'No'}</div>
                        <div className="text-xs opacity-80 mt-0.5">Consent</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Activity Feed (inline in Overview) */}
                <ActivityFeedSection
                  leadId={leadId}
                  activities={activities}
                  actLoading={actLoading}
                  pg={pg}
                  activityPage={activityPage}
                  setActivityPage={setActivityPage}
                  noteText={noteText}
                  setNoteText={setNoteText}
                  remarkType={remarkType}
                  setRemarkType={setRemarkType}
                  handleAddNote={handleAddNote}
                  addRemarkPending={addRemark.isPending}
                  fullPagination={false}
                />
              </div>
            )}

            {/* ── MEDICAL INFO TAB ─────────────────────────────────────── */}
            {activeTab === 'medical' && (
              <div className="p-5">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'Disease / Condition', value: lead.disease, color: 'teal' },
                    { label: 'Treatment',            value: lead.treatment, color: 'blue' },
                    { label: 'Category',             value: lead.category, color: 'indigo' },
                    { label: 'Age',                  value: lead.age ? `${lead.age} years` : null, color: 'slate' },
                    { label: 'OPD Done',             value: lead.opdDone ? '✅ Yes' : '⏳ No', color: lead.opdDone ? 'green' : 'amber' },
                    { label: 'IPD Done',             value: lead.ipdDone ? '✅ Yes' : '⏳ No', color: lead.ipdDone ? 'green' : 'amber' },
                    { label: 'OPD Date',             value: formatDate(lead.opdDate), color: 'cyan' },
                    { label: 'IPD Date',             value: formatDate(lead.ipdDate), color: 'purple' },
                    { label: 'Insurance Provider',   value: lead.insuranceProvider, color: 'orange' },
                    { label: 'Policy Number',        value: lead.policyNumber, color: 'orange' },
                    { label: 'EMI Case',             value: lead.isEmiCase ? 'Yes' : 'No', color: 'blue' },
                    { label: 'Pre-Auth Pending',     value: lead.isPreauthPending ? 'Yes' : 'No', color: 'red' },
                  ].map(item => (
                    <InfoCard key={item.label} label={item.label} value={item.value} color={item.color} />
                  ))}
                </div>
              </div>
            )}

            {/* ── ACTIVITY FEED TAB (full paginated) ───────────────────── */}
            {activeTab === 'activity' && (
              <div className="p-5">
                <ActivityFeedSection
                  leadId={leadId}
                  activities={activities}
                  actLoading={actLoading}
                  pg={pg}
                  activityPage={activityPage}
                  setActivityPage={setActivityPage}
                  noteText={noteText}
                  setNoteText={setNoteText}
                  remarkType={remarkType}
                  setRemarkType={setRemarkType}
                  handleAddNote={handleAddNote}
                  addRemarkPending={addRemark.isPending}
                  fullPagination={true}
                />
              </div>
            )}

            {/* ── DOCUMENTS / CALLS / NOTES placeholders ───────────────── */}
            {(activeTab === 'documents' || activeTab === 'calls' || activeTab === 'notes') && (
              <div className="p-5">
                <EmptyTabState
                  icon={activeTab === 'documents' ? <FileText size={40} /> : activeTab === 'calls' ? <PhoneCall size={40} /> : <Edit2 size={40} />}
                  title={activeTab === 'documents' ? 'Documents' : activeTab === 'calls' ? 'Call History' : 'Notes'}
                  subtitle={
                    activeTab === 'documents' ? 'Manage documents from the Documents section'
                      : activeTab === 'calls' ? 'Call logs are recorded automatically via the Calls module'
                        : 'Use the Activity Feed to add general notes'
                  }
                />
              </div>
            )}
          </div>
        </div>

        {/* ════════════════════ RIGHT SIDEBAR ═════════════════════════════ */}
        <div className="w-72 shrink-0 bg-white border-l border-slate-100 overflow-y-auto p-4 space-y-4">

          {/* Quick Actions */}
          <div>
            <h3 className="flex items-center gap-1.5 text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-3">
              <Zap size={11} className="text-[#00b5d8]" /> Quick Actions
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Call',            icon: <Phone size={16} />,       color: '#10b981', bg: '#ecfdf5',
                  action: () => window.open(`tel:${lead.phone}`) },
                { label: 'WhatsApp',        icon: <MessageCircle size={16} />, color: '#22c55e', bg: '#f0fdf4',
                  action: () => window.open(`https://wa.me/${lead.phone?.replace(/\D/g, '')}`) },
                { label: 'Mark Insurance', icon: <Shield size={16} />,       color: '#f97316', bg: '#fff7ed',
                  action: () => updateLead.mutate({ id: lead.id, isInsuranceCase: true, status: 'insurance' }) },
                { label: 'Mark EMI',        icon: <CreditCard size={16} />,  color: '#3b82f6', bg: '#eff6ff',
                  action: () => updateLead.mutate({ id: lead.id, isEmiCase: true, status: 'emi' }) },
                { label: 'OPD Done',        icon: <Check size={16} />,       color: '#06b6d4', bg: '#ecfeff',
                  action: () => updateLead.mutate({ id: lead.id, opdDone: true, status: 'opd_done' }) },
                { label: 'IPD Done',        icon: <Building2 size={16} />,   color: '#8b5cf6', bg: '#f5f3ff',
                  action: () => updateLead.mutate({ id: lead.id, ipdDone: true, status: 'ipd_done' }) },
              ].map(qa => (
                <button key={qa.label} onClick={qa.action}
                  className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 hover:scale-105 active:scale-95 transition-all"
                  style={{ background: qa.bg, borderColor: qa.color + '30' }}>
                  <span style={{ color: qa.color }}>{qa.icon}</span>
                  <span className="text-[10px] font-bold text-center leading-tight" style={{ color: qa.color }}>{qa.label}</span>
                </button>
              ))}
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Medical Summary */}
          <div>
            <h3 className="flex items-center gap-1.5 text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-3">
              <Stethoscope size={11} className="text-[#00b5d8]" /> Medical Summary
            </h3>
            <div className="space-y-2.5">
              {[
                { label: 'Disease / Condition', value: lead.disease },
                { label: 'Treatment Type',      value: lead.treatment },
                { label: 'Category',            value: lead.category },
                { label: 'OPD Done',            value: lead.opdDone ? 'Completed' : 'Pending' },
                { label: 'IPD Done',            value: lead.ipdDone ? 'Completed' : 'Pending' },
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-400">{item.label}</span>
                  <span className="text-xs font-bold text-slate-700">{item.value ?? '—'}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => setActiveTab('medical')}
              className="w-full mt-3 py-2 text-xs font-bold text-[#00b5d8] border border-[#00b5d8]/30 rounded-xl hover:bg-[#00b5d8]/5 transition-colors">
              View Full Medical Info
            </button>
          </div>

          <hr className="border-slate-100" />

          {/* Documents */}
          <div>
            <h3 className="flex items-center gap-1.5 text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-3">
              <FileText size={11} className="text-[#00b5d8]" /> Documents
            </h3>
            <div className="flex flex-col items-center py-4 bg-slate-50 rounded-xl border border-slate-100 text-center gap-1.5">
              <FileText size={24} className="text-slate-300" />
              <p className="text-xs font-medium text-slate-400">No documents uploaded</p>
              <p className="text-[10px] text-slate-300">Upload from the Documents section</p>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Tags */}
          <div>
            <h3 className="flex items-center gap-1.5 text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-3">
              <Tag size={11} className="text-[#00b5d8]" /> Tags
            </h3>
            {lead.tags?.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {lead.tags.map((t, i) => {
                  const tagColors = [
                    'bg-blue-50 text-blue-700 border-blue-200',
                    'bg-purple-50 text-purple-700 border-purple-200',
                    'bg-red-50 text-red-700 border-red-200',
                    'bg-orange-50 text-orange-700 border-orange-200',
                    'bg-teal-50 text-teal-700 border-teal-200',
                  ];
                  return (
                    <span key={t} className={clsx('px-2.5 py-1 rounded-full text-xs font-semibold border', tagColors[i % tagColors.length])}>
                      {t}
                    </span>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-400">No tags added</p>
            )}
          </div>

          <hr className="border-slate-100" />

          {/* Assigned To */}
          <div>
            <h3 className="flex items-center gap-1.5 text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-3">
              <UserCheck size={11} className="text-[#00b5d8]" /> Assigned To
            </h3>
            {lead.assignedTo ? (() => {
              const av2 = avatarStyle(lead.assignedTo.name);
              return (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold"
                    style={{ background: av2.bg, color: av2.fg }}>
                    {getInitials(lead.assignedTo.name)}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-800">{lead.assignedTo.name}</div>
                    <div className="text-xs text-slate-400 capitalize">{lead.assignedTo.role.replace(/_/g, ' ')}</div>
                  </div>
                </div>
              );
            })() : (
              <p className="text-xs text-slate-400">Unassigned</p>
            )}
          </div>
        </div>
      </div>

      {/* Edit Lead Modal */}
      {showEditModal && lead && (
        <EditLeadModal
          lead={lead}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleEditSubmit}
          isLoading={updateLead.isPending}
        />
      )}
    </div>
  );
}

// ─── Activity Feed Section (shared between Overview + Activity tab) ───────────
interface ActivityFeedSectionProps {
  leadId: string;
  activities: import('@/types').LeadRemark[];
  actLoading: boolean;
  pg: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean } | undefined;
  activityPage: number;
  setActivityPage: (p: number | ((prev: number) => number)) => void;
  noteText: string;
  setNoteText: (v: string) => void;
  remarkType: 'general' | 'call' | 'followup';
  setRemarkType: (v: 'general' | 'call' | 'followup') => void;
  handleAddNote: () => void;
  addRemarkPending: boolean;
  fullPagination: boolean;
}

function ActivityFeedSection({
  activities, actLoading, pg, activityPage, setActivityPage,
  noteText, setNoteText, remarkType, setRemarkType, handleAddNote,
  addRemarkPending, fullPagination,
}: ActivityFeedSectionProps) {
  const displayActivities = fullPagination ? activities : activities.slice(0, 5);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-xs font-bold text-[#1e3a5f] uppercase tracking-wider">
          <Activity size={11} className="text-[#00b5d8]" /> Activity Feed
        </h3>
        <div className="flex items-center gap-1.5">
          {(['general', 'call', 'followup'] as const).map(type => {
            const cfg = ACTIVITY_CFG[type];
            return (
              <button key={type} onClick={() => setRemarkType(type)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all"
                style={remarkType === type
                  ? { background: cfg.color, borderColor: cfg.color, color: '#fff' }
                  : { background: '#fff', borderColor: cfg.color + '40', color: cfg.color }}>
                {cfg.icon}
                {type === 'general' ? 'General Note' : type === 'call' ? 'Call Note' : 'Follow-up'}
              </button>
            );
          })}
        </div>
      </div>

      {/* Note input */}
      <div className="flex gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1e3a5f] to-[#00b5d8] flex items-center justify-center text-white font-bold text-sm shrink-0">
          +
        </div>
        <div className="flex-1 relative">
          <textarea
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAddNote(); }}
            placeholder="Write a note, call outcome, or follow-up reminder… (Ctrl+Enter to save)"
            rows={2}
            className="w-full px-4 py-2.5 text-sm rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#00b5d8]/30 focus:border-[#00b5d8] resize-none"
          />
          <button onClick={handleAddNote} disabled={!noteText.trim() || addRemarkPending}
            className="absolute right-3 bottom-3 flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-gradient-to-r from-[#1e3a5f] to-[#00b5d8] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-all">
            <Send size={11} /> {addRemarkPending ? 'Saving…' : 'Add Note'}
          </button>
        </div>
      </div>

      {/* Activity list */}
      {actLoading ? (
        <div className="flex justify-center py-6"><Spinner /></div>
      ) : displayActivities.length > 0 ? (
        <div className="relative space-y-3">
          {/* Timeline spine */}
          <div className="absolute left-[17px] top-5 bottom-0 w-0.5 bg-gradient-to-b from-slate-200 to-transparent" />

          {displayActivities.map(activity => {
            const cfg = ACTIVITY_CFG[activity.type ?? 'general'] ?? ACTIVITY_CFG.general;
            const isSystem = ['system', 'status_change', 'assignment'].includes(activity.type ?? '');
            const av2 = activity.user ? avatarStyle(activity.user.name) : { fg: '#64748b', bg: '#f1f5f9' };

            return (
              <div key={activity.id} className="flex gap-3">
                {/* Avatar */}
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 z-10 border-2 border-white shadow-sm"
                  style={{ background: isSystem ? cfg.bg : av2.bg, color: isSystem ? cfg.color : av2.fg }}>
                  {isSystem ? cfg.icon : getInitials(activity.user?.name ?? 'S')}
                </div>

                {/* Card */}
                <div className="flex-1 min-w-0 bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                  style={{ borderColor: cfg.color + '25' }}>
                  <div className="flex items-center justify-between px-3 py-2"
                    style={{ background: `linear-gradient(to right, ${cfg.bg}, #fafafa)` }}>
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ background: cfg.color + '20', color: cfg.color }}>
                        {cfg.icon} {cfg.label}
                      </span>
                      <span className="text-xs font-semibold text-slate-700">
                        {activity.user?.name ?? (isSystem ? 'System' : 'Unknown')}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-medium text-slate-600">
                        {activity.user?.name ?? 'System'}
                      </div>
                      <div className="text-[10px] text-slate-400">{formatDateTime(activity.createdAt)}</div>
                    </div>
                  </div>
                  <div className="px-3 py-2.5">
                    <p className="text-sm text-slate-700 leading-relaxed">{activity.remark}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-10">
          <Activity size={32} className="mx-auto text-slate-200 mb-2" />
          <p className="text-slate-400 text-sm">No activity yet. Add the first note above.</p>
        </div>
      )}

      {/* Pagination (full tab only) */}
      {fullPagination && pg && pg.totalPages > 1 && (
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <span className="text-xs text-slate-400">
            {(pg.page - 1) * pg.limit + 1}–{Math.min(pg.page * pg.limit, pg.total)} of {pg.total}
          </span>
          <div className="flex items-center gap-1">
            <PageBtn label="«" disabled={!pg.hasPrev} onClick={() => setActivityPage(1)} />
            <PageBtn label="‹" disabled={!pg.hasPrev} onClick={() => setActivityPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, pg.totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(activityPage - 2, pg.totalPages - 4)) + i;
              return p <= pg.totalPages ? (
                <button key={p} onClick={() => setActivityPage(p)}
                  className={clsx(
                    'w-7 h-7 rounded-lg text-xs font-bold border transition-all',
                    p === pg.page ? 'text-white border-transparent' : 'text-slate-600 hover:bg-slate-50',
                  )}
                  style={p === pg.page ? { background: '#1e3a5f' } : {}}>
                  {p}
                </button>
              ) : null;
            })}
            <PageBtn label="›" disabled={!pg.hasNext} onClick={() => setActivityPage(p => p + 1)} />
            <PageBtn label="»" disabled={!pg.hasNext} onClick={() => setActivityPage(pg.totalPages)} />
          </div>
        </div>
      )}
    </div>
  );
}

function PageBtn({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className="w-7 h-7 flex items-center justify-center rounded-lg border text-xs text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed">
      {label}
    </button>
  );
}

// ─── Info Card ────────────────────────────────────────────────────────────────
const COLOR_MAP: Record<string, string> = {
  teal: 'bg-teal-50 border-teal-100', blue: 'bg-blue-50 border-blue-100',
  green: 'bg-emerald-50 border-emerald-100', amber: 'bg-amber-50 border-amber-100',
  cyan: 'bg-cyan-50 border-cyan-100', purple: 'bg-purple-50 border-purple-100',
  orange: 'bg-orange-50 border-orange-100', red: 'bg-red-50 border-red-100',
  indigo: 'bg-indigo-50 border-indigo-100', slate: 'bg-slate-50 border-slate-100',
};

function InfoCard({ label, value, color = 'slate' }: { label: string; value: string | null | undefined; color?: string }) {
  return (
    <div className={clsx('rounded-xl p-3 border', COLOR_MAP[color] ?? COLOR_MAP.slate)}>
      <div className="text-xs text-slate-400 mb-0.5">{label}</div>
      <div className="text-sm font-bold text-slate-800">{value ?? '—'}</div>
    </div>
  );
}

// ─── Empty Tab State ──────────────────────────────────────────────────────────
function EmptyTabState({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) {
  return (
    <div className="text-center py-16">
      <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4 text-slate-200">
        {icon}
      </div>
      <p className="text-slate-600 font-semibold">{title}</p>
      <p className="text-slate-400 text-sm mt-1">{subtitle}</p>
    </div>
  );
}
